import { useState } from "react";
import { ArrowLeft, Plus, Edit3, X, Save, Pencil } from "lucide-react";
import { uid } from "../utils/storage";
import { todayStr } from "../utils/dateHelpers";
import { S } from "../theme/sharedStyles";
import { LINK_CATS } from "../constants/misc";
import { TabHeader } from "../components/TabHeader";
import { Card } from "../components/Card";
import Modal from "../components/Modal";
import { LinkEditor } from "../components/LinkEditor";
import { NativePickerInput } from "../components/NativePickerInput";

export function TasksTab({ tasks, setTasks, categories, initialDate = null, onBack = null }) {
  const [showForm, setShowForm] = useState(true);
  const [newSub, setNewSub] = useState("");
  const emptyForm = { title: "", goalId: "", projectType: "", projectName: "", startDate: initialDate || "", dueDate: initialDate || "", frequencyType: "once", frequencyDays: [], dayLabels: "named", monthDays: [], notes: "", subtasks: [], autoPopulate: true, links: [] };
  const [form, setForm] = useState(emptyForm);

  const addTask = () => {
    if (!form.title.trim()) return;
    setTasks(p => [...p, { id: uid(), ...form, done: false, createdAt: todayStr() }]);
    setForm(emptyForm);
    setShowForm(false);
  };

  const toggleTask = id => setTasks(p => p.map(t => t.id === id ? { ...t, done: !t.done, completedAt: !t.done ? new Date().toISOString() : null } : t));
  const toggleSub = (taskId, subId) => setTasks(p => p.map(t => t.id === taskId ? { ...t, subtasks: t.subtasks.map(s => s.id === subId ? { ...s, done: !s.done } : s) } : t));
  const deleteTask = id => { setTasks(p => p.filter(t => t.id !== id)); };

  const [editingTask, setEditingTask] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState(null);
  const [editNewSub, setEditNewSub] = useState("");

  const openEditTask = (task) => {
    setEditingTask(task.id);
    setEditTaskForm({ title: task.title, priority: task.priority || "medium", goalId: task.goalId || "", projectType: task.projectType || "", projectName: task.projectName || "", startDate: task.startDate || "", dueDate: task.dueDate || "", frequencyType: task.frequencyType || "once", frequencyDays: task.frequencyDays || [], dayLabels: task.dayLabels || "named", monthDays: task.monthDays || [], notes: task.notes || "", subtasks: task.subtasks || [], autoPopulate: task.autoPopulate !== false, links: task.links || [] });
    setEditNewSub("");
  };

  const saveEditTask = () => {
    if (!editTaskForm.title.trim()) return;
    setTasks(p => p.map(t => t.id === editingTask ? { ...t, ...editTaskForm } : t));
    setEditingTask(null);
    setEditTaskForm(null);
  };

  const cancelEditTask = () => { setEditingTask(null); setEditTaskForm(null); setEditNewSub(""); };

  const filterTasks = () => {
    return tasks;
  };

  const filtered = filterTasks();


  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {onBack && <button onClick={onBack} style={S.btn}><ArrowLeft size={14} style={{marginRight:4}} />Back to Calendar</button>}
      <TabHeader
        title="Tasks"
        gradient="linear-gradient(125deg, #e05030 0%, #d08020 100%)"
        subtextColor="#c86020"
        subtext={tasks.filter(t => !t.done).length === 0 ? "All clear!" : `${tasks.filter(t => !t.done).length} pending`}
      />

      {/* FAB */}
      <button
        onClick={() => { setForm(emptyForm); setShowForm(true); }}
        className="clarity-add-btn"
        title="New task"
        style={{
          position: "fixed", bottom: "calc(88px + var(--sab, 0px))", right: 20, zIndex: 200,
          width: 52, height: 52, borderRadius: "50%", padding: 0,
          background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))",
          boxShadow: "0 4px 20px rgba(200,96,32,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center",
          border: "none", cursor: "pointer",
        }}
      >
        <Plus size={22} color="var(--c-on-primary)" strokeWidth={2.5} />
      </button>

      {showForm && (
        <Card title="New task">
          <input placeholder="Task title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} style={S.input} />

          <p style={{ ...S.label, marginTop: 10 }}>LINK TO</p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[["","None"],["goal","Goal"],["project","Project"]].map(([val, lbl]) => (
              <button key={lbl} onClick={() => setForm(p => ({ ...p, projectType: val, goalId: "", projectName: "" }))}
                style={{ ...S.btn, fontSize: 12, background: form.projectType === val ? "var(--c-bg-card)" : "transparent", color: form.projectType === val ? "#d4789a" : "#a07090" }}>{lbl}</button>
            ))}
          </div>
          {form.projectType === "goal" && (
            <select value={form.goalId} onChange={e => setForm(p => ({ ...p, goalId: e.target.value }))} style={{ ...S.input, marginTop: 6 }}>
              <option value="">Select category…</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.title}</option>)}
            </select>
          )}
          {form.projectType === "project" && (
            <div style={{ marginTop: 6 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                {["Work","Personal","School"].map(p => (
                  <button key={p} onClick={() => setForm(f => ({ ...f, projectName: p }))}
                    style={{ ...S.btn, fontSize: 11, background: form.projectName === p ? "var(--c-bg-card)" : "transparent", color: form.projectName === p ? "#d4789a" : "#a07090" }}>{p}</button>
                ))}
              </div>
              <input placeholder="Or type a custom project name…" value={form.projectName} onChange={e => setForm(p => ({ ...p, projectName: e.target.value }))} style={S.input} />
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <div style={{ flex: 1 }}>
              <p style={S.label}>START</p>
              <NativePickerInput type="date" value={form.startDate} onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} style={S.input} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={S.label}>DUE</p>
              <NativePickerInput type="date" value={form.dueDate} onChange={e => setForm(p => ({ ...p, dueDate: e.target.value }))} style={S.input} />
            </div>
          </div>

          <p style={{ ...S.label, marginTop: 10 }}>REPEAT</p>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {["once","daily","weekly","monthly"].map(f => (
              <button key={f} onClick={() => setForm(p => ({ ...p, frequencyType: f }))}
                style={{ ...S.btn, fontSize: 12, background: form.frequencyType === f ? "var(--c-bg-card)" : "transparent", color: form.frequencyType === f ? "#d4789a" : "#a07090", textTransform: "capitalize" }}>{f}</button>
            ))}
          </div>

          {form.frequencyType === "weekly" && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                  <button key={d} onClick={() => setForm(p => ({ ...p, frequencyDays: p.frequencyDays.includes(i) ? p.frequencyDays.filter(x => x !== i) : [...p.frequencyDays, i] }))}
                    style={{ ...S.btn, fontSize: 11, background: form.frequencyDays.includes(i) ? "var(--c-bg-card)" : "transparent", color: form.frequencyDays.includes(i) ? "#d4789a" : "#a07090" }}>{d}</button>
                ))}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 12, color: "var(--c-text-mid)", cursor: "pointer" }}>
                <input type="checkbox" checked={form.autoPopulate} onChange={e => setForm(p => ({ ...p, autoPopulate: e.target.checked }))} />
                Auto-populate in today's list
              </label>
            </div>
          )}

          {form.frequencyType === "monthly" && (
            <div style={{ marginTop: 8, display: "flex", gap: 3, flexWrap: "wrap" }}>
              {Array.from({ length: 31 }, (_, i) => (
                <button key={i+1} onClick={() => setForm(p => ({ ...p, monthDays: p.monthDays.includes(i+1) ? p.monthDays.filter(x => x !== i+1) : [...p.monthDays, i+1] }))}
                  style={{ ...S.btn, fontSize: 11, width: 32, padding: "3px 0", background: form.monthDays.includes(i+1) ? "var(--c-bg-card)" : "transparent", color: form.monthDays.includes(i+1) ? "#d4789a" : "#a07090" }}>{i+1}</button>
              ))}
            </div>
          )}

          <p style={{ ...S.label, marginTop: 12 }}>SUBTASKS</p>
          {form.subtasks.map(s => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", fontSize: 13 }}>
              <span style={{ flex: 1 }}>• {s.title}</span>
              <button onClick={() => setForm(p => ({ ...p, subtasks: p.subtasks.filter(x => x.id !== s.id) }))} style={{ fontSize: 11, color: "#e8647a", background: "transparent", border: "none", cursor: "pointer" }}><X size={16} /></button>
            </div>
          ))}
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <input placeholder="Add subtask…" value={newSub} onChange={e => setNewSub(e.target.value)} style={{ ...S.input, flex: 1 }}
              onKeyDown={e => { if (e.key === "Enter" && newSub.trim()) { setForm(p => ({ ...p, subtasks: [...p.subtasks, { id: uid(), title: newSub.trim(), done: false }] })); setNewSub(""); } }} />
            <button onClick={() => { if (newSub.trim()) { setForm(p => ({ ...p, subtasks: [...p.subtasks, { id: uid(), title: newSub.trim(), done: false }] })); setNewSub(""); } }} className="clarity-add-btn" style={S.addBtn}><Plus size={13} style={{marginRight:4}} />Add</button>
          </div>

          <textarea placeholder="Notes…" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            style={{ ...S.input, marginTop: 8, height: 60, resize: "none" }} />

          <LinkEditor links={form.links || []} onChange={links => setForm(p => ({ ...p, links }))} />

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={addTask} className="clarity-add-btn" style={S.addBtn}><Save size={14} style={{marginRight:4}} />Save task</button>
            <button onClick={() => { setShowForm(false); setForm(emptyForm); }} style={S.btn}><X size={14} style={{marginRight:4}} />Cancel</button>
          </div>
        </Card>
      )}

      {filtered.length === 0 && <p style={S.empty}>No tasks here</p>}

      {/* Edit Task Modal */}
      {editingTask && editTaskForm && (
        <Modal onClose={cancelEditTask}>
          <div style={{ background: "var(--c-bg-card)", borderRadius: 18, padding: 24, width: "100%", maxWidth: 440, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 32px rgba(180,80,120,0.18)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--c-text)" }}><Edit3 size={16} style={{marginRight:6}} />Edit Task</h3>
              <button onClick={cancelEditTask} style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--c-text-soft)" }}><X size={16} /></button>
            </div>

            <input placeholder="Task title" value={editTaskForm.title} onChange={e => setEditTaskForm(p => ({ ...p, title: e.target.value }))} style={S.input} />

            <p style={{ ...S.label, marginTop: 10 }}>LINK TO</p>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[["","None"],["goal","Goal"],["project","Project"]].map(([val, lbl]) => (
                <button key={lbl} onClick={() => setEditTaskForm(p => ({ ...p, projectType: val, goalId: "", projectName: "" }))}
                  style={{ ...S.btn, fontSize: 12, background: editTaskForm.projectType === val ? "var(--c-bg-card)" : "transparent", color: editTaskForm.projectType === val ? "#d4789a" : "#a07090" }}>{lbl}</button>
              ))}
            </div>
            {editTaskForm.projectType === "goal" && (
              <select value={editTaskForm.goalId} onChange={e => setEditTaskForm(p => ({ ...p, goalId: e.target.value }))} style={{ ...S.input, marginTop: 6 }}>
                <option value="">Select category…</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.title}</option>)}
              </select>
            )}
            {editTaskForm.projectType === "project" && (
              <div style={{ marginTop: 6 }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                  {["Work","Personal","School"].map(p => (
                    <button key={p} onClick={() => setEditTaskForm(f => ({ ...f, projectName: p }))}
                      style={{ ...S.btn, fontSize: 11, background: editTaskForm.projectName === p ? "var(--c-bg-card)" : "transparent", color: editTaskForm.projectName === p ? "#d4789a" : "#a07090" }}>{p}</button>
                  ))}
                </div>
                <input placeholder="Or type a custom project name…" value={editTaskForm.projectName} onChange={e => setEditTaskForm(p => ({ ...p, projectName: e.target.value }))} style={S.input} />
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <div style={{ flex: 1 }}>
                <p style={S.label}>START</p>
                <NativePickerInput type="date" value={editTaskForm.startDate} onChange={e => setEditTaskForm(p => ({ ...p, startDate: e.target.value }))} style={S.input} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={S.label}>DUE</p>
                <NativePickerInput type="date" value={editTaskForm.dueDate} onChange={e => setEditTaskForm(p => ({ ...p, dueDate: e.target.value }))} style={S.input} />
              </div>
            </div>

            <p style={{ ...S.label, marginTop: 10 }}>REPEAT</p>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {["once","daily","weekly","monthly"].map(f => (
                <button key={f} onClick={() => setEditTaskForm(p => ({ ...p, frequencyType: f }))}
                  style={{ ...S.btn, fontSize: 12, background: editTaskForm.frequencyType === f ? "var(--c-bg-card)" : "transparent", color: editTaskForm.frequencyType === f ? "#d4789a" : "#a07090", textTransform: "capitalize" }}>{f}</button>
              ))}
            </div>
            {editTaskForm.frequencyType === "weekly" && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                    <button key={d} onClick={() => setEditTaskForm(p => ({ ...p, frequencyDays: p.frequencyDays.includes(i) ? p.frequencyDays.filter(x => x !== i) : [...p.frequencyDays, i] }))}
                      style={{ ...S.btn, fontSize: 11, background: editTaskForm.frequencyDays.includes(i) ? "var(--c-bg-card)" : "transparent", color: editTaskForm.frequencyDays.includes(i) ? "#d4789a" : "#a07090" }}>{d}</button>
                  ))}
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 12, color: "var(--c-text-mid)", cursor: "pointer" }}>
                  <input type="checkbox" checked={editTaskForm.autoPopulate} onChange={e => setEditTaskForm(p => ({ ...p, autoPopulate: e.target.checked }))} />
                  Auto-populate in today's list
                </label>
              </div>
            )}
            {editTaskForm.frequencyType === "monthly" && (
              <div style={{ marginTop: 8, display: "flex", gap: 3, flexWrap: "wrap" }}>
                {Array.from({ length: 31 }, (_, i) => (
                  <button key={i+1} onClick={() => setEditTaskForm(p => ({ ...p, monthDays: p.monthDays.includes(i+1) ? p.monthDays.filter(x => x !== i+1) : [...p.monthDays, i+1] }))}
                    style={{ ...S.btn, fontSize: 11, width: 32, padding: "3px 0", background: editTaskForm.monthDays.includes(i+1) ? "var(--c-bg-card)" : "transparent", color: editTaskForm.monthDays.includes(i+1) ? "#d4789a" : "#a07090" }}>{i+1}</button>
                ))}
              </div>
            )}

            <p style={{ ...S.label, marginTop: 12 }}>SUBTASKS</p>
            {editTaskForm.subtasks.map(s => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 0", fontSize: 13 }}>
                <span style={{ flex: 1 }}>• {s.title}</span>
                <button onClick={() => setEditTaskForm(p => ({ ...p, subtasks: p.subtasks.filter(x => x.id !== s.id) }))} style={{ fontSize: 11, color: "#e8647a", background: "transparent", border: "none", cursor: "pointer" }}><X size={16} /></button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              <input placeholder="Add subtask…" value={editNewSub} onChange={e => setEditNewSub(e.target.value)} style={{ ...S.input, flex: 1 }}
                onKeyDown={e => { if (e.key === "Enter" && editNewSub.trim()) { setEditTaskForm(p => ({ ...p, subtasks: [...p.subtasks, { id: uid(), title: editNewSub.trim(), done: false }] })); setEditNewSub(""); } }} />
              <button onClick={() => { if (editNewSub.trim()) { setEditTaskForm(p => ({ ...p, subtasks: [...p.subtasks, { id: uid(), title: editNewSub.trim(), done: false }] })); setEditNewSub(""); } }} className="clarity-add-btn" style={S.addBtn}><Plus size={13} style={{marginRight:4}} />Add</button>
            </div>

            <textarea placeholder="Notes…" value={editTaskForm.notes} onChange={e => setEditTaskForm(p => ({ ...p, notes: e.target.value }))}
              style={{ ...S.input, marginTop: 8, height: 60, resize: "none" }} />

            <LinkEditor links={editTaskForm.links || []} onChange={links => setEditTaskForm(p => ({ ...p, links }))} />

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={saveEditTask} className="clarity-add-btn" style={S.addBtn}><Save size={14} style={{marginRight:4}} />Save changes</button>
              <button onClick={cancelEditTask} style={S.btn}><X size={14} style={{marginRight:4}} />Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {filtered.map((task, _i) => {
        const goal = categories ? categories.find(c => c.id === task.goalId) : null;
        const isMissing = !task.done && task.dueDate && task.dueDate < todayStr();
        return (
          <div key={task.id} className="clarity-row-item clarity-hover-card" style={{ background: "var(--c-bg-card)", borderRadius: 16, padding: "12px 14px", border: `1.5px solid ${isMissing ? "#f5c0c8" : "#f5d8ec"}`, boxShadow: "0 2px 10px rgba(212,120,154,0.06)", animationDelay: `${_i * 0.04}s` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div onClick={() => toggleTask(task.id)}
                style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid #d4789a`, background: task.done ? "#d4789a" : "transparent", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13 }}>
                {task.done ? "✓" : ""}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, textDecoration: task.done ? "line-through" : "none", opacity: task.done ? 0.5 : 1 }}>{task.title}</p>
                <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                  {task.dueDate && <span style={{ fontSize: 10, color: isMissing ? "#e8647a" : "#a07090", background: isMissing ? "#fde8ea" : "#f5eaf0", borderRadius: 6, padding: "2px 6px" }}>{isMissing ? "⚠️" : "📅"} {task.dueDate}</span>}
                  {goal && <span style={{ fontSize: 10, color: "var(--c-text-soft)", background: "var(--c-bg-soft)", borderRadius: 6, padding: "2px 6px" }}>{goal.emoji} {goal.title || goal.name}</span>}
                  {task.projectName && <span style={{ fontSize: 10, color: "#7060a0", background: "var(--c-bg-soft)", borderRadius: 6, padding: "2px 6px" }}>📁 {task.projectName}</span>}
                  {task.frequencyType !== "once" && <span style={{ fontSize: 10, color: "var(--c-text-soft)", background: "var(--c-bg-soft)", borderRadius: 6, padding: "2px 6px", textTransform: "capitalize" }}>↻ {task.frequencyType}</span>}
                </div>
              </div>
              <button onClick={() => openEditTask(task)} style={{ fontSize: 13, color: "var(--c-text-soft)", background: "transparent", border: "none", cursor: "pointer", padding: "0 3px" }} title="Edit task"><Pencil size={14} /></button>
              <button onClick={() => deleteTask(task.id)} style={{ fontSize: 11, color: "#e8647a", background: "transparent", border: "none", cursor: "pointer", padding: 4 }}><X size={16} /></button>
            </div>
            {task.notes && <p className="clarity-notes-view" style={{ margin: "8px 0 0", fontSize: 12, color: "var(--c-text-soft)", paddingLeft: 32 }} dangerouslySetInnerHTML={{ __html: task.notes }} />}
            {task.subtasks?.length > 0 && (
              <div style={{ paddingLeft: 32, marginTop: 8 }}>
                {task.subtasks.map(s => (
                  <div key={s.id} onClick={() => toggleSub(task.id, s.id)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer" }}>
                    <div style={{ width: 14, height: 14, borderRadius: 4, border: "1.5px solid #d4789a", background: s.done ? "#d4789a" : "transparent", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 9 }}>{s.done ? "✓" : ""}</div>
                    <span style={{ fontSize: 12, textDecoration: s.done ? "line-through" : "none", opacity: s.done ? 0.5 : 1 }}>{s.title}</span>
                  </div>
                ))}
              </div>
            )}
            {task.links?.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, paddingLeft: 32, marginTop: 8 }}>
                {task.links.map(l => {
                  const meta = LINK_CATS.find(c => c.key === l.cat);
                  const display = l.label || meta?.label || l.cat;
                  return (
                    <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer"
                      style={{ display: "flex", alignItems: "center", gap: 4, background: "var(--c-bg-soft)", border: "1.5px solid #f0c8e0", borderRadius: 20, padding: "3px 10px", fontSize: 11, color: "var(--c-text-mid)", fontWeight: 600, textDecoration: "none" }}>
                      <span>{meta?.emoji || "🔗"}</span>
                      <span>{display}</span>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
